import {
  closestCenter,
  DndContext,
  DragEndEvent,
  DragStartEvent,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  UniqueIdentifier,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import {
  arrayMove,
  SortableContext as DndSortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import {
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { StyledTr } from "../../components/Table/styles";
import { withDraggableProps } from "./withDraggableProps";
import { useGrabbingCursor } from "./useGrabbingCursor";
import { isDeepEqual } from "remeda";
import { AssignedTableRow } from "components/patients/patientCard/icbt/useIcbtTableData/types";

type UseDnDProps<T> = {
  data: T[];
  onDragEnd: (data: T[]) => void;
};

export const useDnD = <T extends AssignedTableRow>({
  data,
  onDragEnd,
}: UseDnDProps<T>) => {
  const { setCursor, resetCursor } = useGrabbingCursor();

  const [tableData, setTableData] = useState<T[]>([]);

  const prevDataIds = useRef<UniqueIdentifier[]>([]);

  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);

  useEffect(() => {
    setTableData(data);
  }, [data]);

  const sensors = useSensors(
    useSensor(MouseSensor),
    useSensor(TouchSensor),
    useSensor(KeyboardSensor),
  );

  const dataIds = useMemo<UniqueIdentifier[]>(
    () => tableData.map((row) => row.id),
    [tableData],
  );

  const handleDragStart = useCallback(
    ({ active }: DragStartEvent) => {
      setCursor();
      setActiveId(active.id);
    },
    [setCursor],
  );

  const handleChange = useCallback(
    (data: T[]) => {
      const currentIds = data?.map((row) => row.id) || [];
      const diff = isDeepEqual(prevDataIds.current, currentIds);
      if (!diff) {
        prevDataIds.current = currentIds;
        onDragEnd(data);
      }
    },
    [onDragEnd],
  );

  useEffect(() => {
    handleChange(tableData);
  }, [handleChange, tableData]);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (active && over && active.id !== over.id) {
        setTableData((data) => {
          const oldIndex = dataIds.indexOf(active.id);
          const newIndex = dataIds.indexOf(over.id);
          return arrayMove(data, oldIndex, newIndex);
        });
      }
      setActiveId(null);
      resetCursor();
    },

    [dataIds, resetCursor],
  );

  const RootContext = useCallback(
    (props: PropsWithChildren) => (
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        modifiers={[restrictToVerticalAxis]}
        onDragEnd={handleDragEnd}
        onDragStart={handleDragStart}
      >
        {props.children}
      </DndContext>
    ),
    [handleDragEnd, handleDragStart, sensors],
  );

  const DraggableRow = withDraggableProps(StyledTr, { items: data, activeId });

  const SortableContext = useCallback(
    (props: PropsWithChildren) => (
      <DndSortableContext
        items={dataIds}
        strategy={verticalListSortingStrategy}
      >
        {props.children}
      </DndSortableContext>
    ),
    [dataIds],
  );

  return {
    RootContext,
    SortableContext,
    RowComponent: DraggableRow,
    data: tableData,
  };
};
